import React, { useEffect, useState } from 'react';
import {
    Button,
    ButtonContainer,
    Heading,
    Icon,
    IconSize,
    Layout,
    Modal,
    Paragraph,
    TileSelect,
    TileSelectGroup,
    Notification,
} from '@vwfs-bronson/bronson-react';
import {
    Contract,
    ContractDetails,
    ContractEndOptionsDetails,
    ContractEndOptionsEN,
    getEndOptionEndpoint,
    MarketError,
    MyProfileData,
} from 'cpfr-common';
import { ContractHeader, NotificationStatus, Spinner } from '@cp-shared-19/frontend-ui';
import { useTranslation } from 'react-i18next';
import { LicensePlate } from 'components/license-plate/LicensePlate';
import { dashboardPagePath } from 'components/navigation/paths';
import { useHistory } from 'react-router-dom';
import { CpDataApi } from 'cp-xhr';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import { useUpdateContractsAll } from '../../useUpdateContractsAll';
import { TFunction } from 'i18next';
import { EmailInputModal } from '../../../request/form-view/email-input/EmailInputModal';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';

type ContractEndOptionsRequestProps = {
    isError: boolean;
    isSuccess: boolean;
    optionValue: ContractEndOptionsEN | undefined;
};

enum OptionsView {
    MAIN = 'MAIN',
    BUY_A_CAR = 'BUY_A_CAR',
    REFINANCE_ADDITIONAL = 'REFINANCE_ADDITIONAL',
}

export const ContractEndOptionsUi: React.FC<{ contract?: Contract & ContractDetails; myProfile?: MyProfileData }> = ({
    contract,
    myProfile,
}) => {
    const { t } = useTranslation(['contract-end-options', 'loader']);
    const { t: cms } = useTranslation('contract-end-options-cms');
    const history = useHistory();
    const [currentOptions, setCurrentOptions] = useState<OptionsView>(OptionsView.MAIN);
    const [contractEndOptionsRequest, setContractEndOptionsRequest] = useState<ContractEndOptionsRequestProps>({
        isError: false,
        isSuccess: false,
        optionValue: undefined,
    });
    const { isSubmitting, run } = useSubmitOnce();
    const [showEmailModal, setShowEmailModal] = useState(false);

    const { updateContracts, contractsData } = useUpdateContractsAll();

    if (!contract) {
        return (
            <Notification status={NotificationStatus.error} title={''} hideTitle={true} visible showCloseButton={false}>
                {t('error.text')}
            </Notification>
        );
    }

    const { contractNumber, carModel = ' ', licensePlate = '', contractId, productTypeFR = '' } = contract;

    const { brand: contractBrand } = contract;
    const brand = contractBrand ? contractBrand.toLowerCase() : null;
    const brandLabel = brand === 'vwcv' ? 'Volkswagen Véhicules Utilitaires' : contractBrand;
    const isRetail = myProfile?.identification?.customerType === 'Retail';

    let vehicleRestitutionGuideBrand: null | string = null;
    switch (brand) {
        case 'vwfs':
            vehicleRestitutionGuideBrand = 'vw';
            break;
        case 'vwcv':
            vehicleRestitutionGuideBrand = null;
            break;
        default:
            vehicleRestitutionGuideBrand = brand;
            break;
    }

    let loalldPdfSize: null | string = null;
    switch (vehicleRestitutionGuideBrand) {
        case 'audi':
            loalldPdfSize = '1 MB';
            break;
        case 'seat':
            loalldPdfSize = '2 MB';
            break;
        case 'skoda':
            loalldPdfSize = '3 MB';
            break;
        case 'vw':
            loalldPdfSize = '3 MB';
            break;
    }

    const showSubmitButton = (contract: Contract & ContractDetails) => {
        if (contract.isEligibleRenewalContractNotificationForLessOfTwelveMonths) return false;
        return true;
    };

    const getSuccessText = (
        t: TFunction,
        contractEndOptionsRequest: ContractEndOptionsRequestProps,
        contract: Contract & ContractDetails,
    ) => {
        // Option 1 (renew) - 4 / 2 / 1 months
        if (
            contractEndOptionsRequest.optionValue === ContractEndOptionsEN.RENEWAL &&
            (contract.isEligibleRenewalContractNotificationForLessOfFourMonths ||
                contract.isEligibleRenewalContractNotificationForLessOfOneMonth ||
                contract.isEligibleRenewalContractNotificationForLessOfTwoMonths)
        ) {
            return (
                <Layout>
                    <Layout.Item>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('success.text-renewal-4-2-1months'),
                            }}
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('success.text2-renewal-4-2-1months'),
                            }}
                        />
                    </Layout.Item>
                </Layout>
            );
        }
        // Option 2 (buy) - 4 months
        if (
            (contractEndOptionsRequest.optionValue === ContractEndOptionsEN.PAYMENT ||
                contractEndOptionsRequest.optionValue === ContractEndOptionsEN.REFINANCING) &&
            contract.isEligibleRenewalContractNotificationForLessOfFourMonths
        ) {
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: t('success.text-buy-a-car-4months', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            );
        }
        if (
            contractEndOptionsRequest.optionValue === ContractEndOptionsEN.REFINANCING &&
            (contract.isEligibleRenewalContractNotificationForLessOfOneMonth ||
                contract.isEligibleRenewalContractNotificationForLessOfTwoMonths)
        ) {
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: t('success.refinancing', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            );
        }

        if (
            contractEndOptionsRequest.optionValue === ContractEndOptionsEN.PAYMENT &&
            (contract.isEligibleRenewalContractNotificationForLessOfOneMonth ||
                contract.isEligibleRenewalContractNotificationForLessOfTwoMonths)
        ) {
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: t('success.payment', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            );
        }
        // Option 3 (return)- 4 / 2 / 1 months
        if (
            contractEndOptionsRequest.optionValue === ContractEndOptionsEN.RETURN &&
            (contract.isEligibleRenewalContractNotificationForLessOfFourMonths ||
                contract.isEligibleRenewalContractNotificationForLessOfOneMonth ||
                contract.isEligibleRenewalContractNotificationForLessOfTwoMonths)
        ) {
            return (
                <span
                    dangerouslySetInnerHTML={{
                        __html: t('success.text-return-4-2-1months', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            );
        }

        return t('success.text');
    };

    const updateIsEligibleRenewalFlag = (): void => {
        const updatedContractsData = [...contractsData].map((contract) => {
            if (contract.contractId === contractId) {
                const updatedContract = { ...contract };
                updatedContract.isEligibleRenewalContractNotificationForLessOfFourMonths = false;
                updatedContract.isEligibleRenewalContractNotificationForLessOfOneMonth = false;
                updatedContract.isEligibleRenewalContractNotificationForLessOfThreeMonths = false;
                updatedContract.isEligibleRenewalContractNotificationForLessOfTwelveMonths = false;
                updatedContract.isEligibleRenewalContractNotificationForLessOfTwoMonths = false;
                updatedContract.isEligibleRenewalContractNotificationForLessOneMonth = false;
                updatedContract.isEligibleRenewalContractNotificationForLoiLagarde = false;
                return updatedContract;
            }
            return contract;
        });
        updateContracts(updatedContractsData);
    };

    const handleModalClose = () => {
        setContractEndOptionsRequest({
            isError: false,
            isSuccess: false,
            optionValue: undefined,
        });
    };

    const redirectToDashboard = () => {
        updateIsEligibleRenewalFlag();
        history.push(dashboardPagePath());
    };

    const handleChange = (option: ContractEndOptionsEN) => {
        setContractEndOptionsRequest({
            ...contractEndOptionsRequest,
            optionValue: option,
        });
    };

    const clearChosenOption = () => {
        setContractEndOptionsRequest({
            ...contractEndOptionsRequest,
            optionValue: undefined,
        });
    };

    const handleSubmit = (overrideEmail?: string): Promise<void> =>
        run(async () => {
            if (contractEndOptionsRequest.optionValue === undefined) {
                return;
            }

            const preparedData: ContractEndOptionsDetails = {
                email: overrideEmail || (myProfile?.contactDetails?.email ? myProfile?.contactDetails?.email : ''),
                endOption: contractEndOptionsRequest.optionValue,
            };

            // Returned, so the latch is only released once the request answered.
            return CpDataApi.put(getEndOptionEndpoint(contract.contractId), preparedData)
                .then(() => {
                    setContractEndOptionsRequest({ ...contractEndOptionsRequest, isSuccess: true });
                })
                .catch((error) => {
                    // A duplicate is not a failure: the option change is already registered.
                    if (isDuplicateSubmissionError(error)) {
                        setContractEndOptionsRequest({ ...contractEndOptionsRequest, isSuccess: true });
                        return;
                    }
                    // Read through getErrorCode: a timeout or a network drop has no
                    // `response`, and reading into it here threw before the user was
                    // ever told the request failed.
                    if (getErrorCode(error) === MarketError.ERROR_NO_EMAIL) {
                        setShowEmailModal(true);
                    } else {
                        setContractEndOptionsRequest({ ...contractEndOptionsRequest, isError: true });
                    }
                });
        });

    const refinancingOption = formatCpDate(contract.returnBackVehicleDate)
        .toMoment()
        .subtract(1, 'months')
        .isBefore(new Date()) ? (
        <TileSelect
            icon="semantic-calculator"
            title={cms('options.refinancing.headline')}
            inputType="radio"
            radioGroupName="buyACarOptions"
            onClick={() => handleChange(ContractEndOptionsEN.REFINANCING)}
            testId={'tile-select-refinancing'}
        >
            {cms('options.refinancing.description')}
        </TileSelect>
    ) : undefined;

    useEffect(() => {
        const styleId = 'tile-select-link-fix';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `.c-tile-select__input:checked ~ .c-tile-select__body-content a { color: inherit; }`;
            document.head.appendChild(style);
        }
        return () => {
            document.getElementById(styleId)?.remove();
        };
    }, []);

    return (
        <>
            {isSubmitting && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Layout>
                <Layout.Item default="1/1" s="1/1">
                    <ContractHeader
                        contractIdentifier={<LicensePlate registrationNumber={licensePlate} />}
                        carInformation={carModel}
                        contractCategory={productTypeFR}
                        contractNumber={contractNumber}
                    />
                </Layout.Item>
                {/* contract.isEligibleRenewalContractNotificationForLessOfTwelveMonths */}
                <Layout.Item>
                    {currentOptions === OptionsView.MAIN &&
                        contract.isEligibleRenewalContractNotificationForLessOfTwelveMonths && (
                            <RenewEndOption12Months brand={brandLabel || ''} />
                        )}
                    {/* 4 2 1 Months */}
                    {currentOptions === OptionsView.MAIN && (
                        <>
                            {contract.isEligibleRenewalContractNotificationForLessOfFourMonths ||
                            contract.isEligibleRenewalContractNotificationForLessOfTwoMonths ||
                            contract.isEligibleRenewalContractNotificationForLessOfOneMonth ? (
                                <TileSelectGroup
                                    title={{ text: t('options.headline') }}
                                    key={OptionsView.MAIN}
                                    layoutItemClassName="u-1/3 u-1/2@l u-1/1@s"
                                    className={'u-text-center'}
                                    testId={'tile-select-group-main'}
                                >
                                    <RenewEndOption421Months onClick={handleChange} brand={brandLabel || ''} />
                                    <BuyEndOption4m2m1m
                                        contract={contract}
                                        onClick={
                                            contract.isEligibleRenewalContractNotificationForLessOfFourMonths
                                                ? () => {
                                                      handleChange(ContractEndOptionsEN.PAYMENT);
                                                  }
                                                : () => {
                                                      clearChosenOption();
                                                      setCurrentOptions(OptionsView.BUY_A_CAR);
                                                  }
                                        }
                                    />
                                    <ReturnEndOption4m2m1m
                                        onClick={() => handleChange(ContractEndOptionsEN.RETURN)}
                                        one={
                                            !!(
                                                contract.isEligibleRenewalContractNotificationForLessOfOneMonth ||
                                                contract.isEligibleRenewalContractNotificationForLessOfTwoMonths
                                            )
                                        }
                                    />
                                </TileSelectGroup>
                            ) : (
                                <>
                                    {!contract.isEligibleRenewalContractNotificationForLessOfTwelveMonths && (
                                        <TileSelectGroup
                                            title={{ text: t('options.headline') }}
                                            key={OptionsView.MAIN}
                                            layoutItemClassName="u-1/3 u-1/2@l u-1/1@s"
                                            className={'u-text-center'}
                                            testId={'tile-select-group-main'}
                                        >
                                            <TileSelect
                                                icon="semantic-car"
                                                title={cms('options.renewal.headline')}
                                                inputType="radio"
                                                radioGroupName="contractEndMainOptions"
                                                onClick={() => handleChange(ContractEndOptionsEN.RENEWAL)}
                                            >
                                                {cms('options.renewal.description')}
                                                {isRetail && vehicleRestitutionGuideBrand && (
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: cms('options.renewal.vehicle-restitution-guide', {
                                                                brand: vehicleRestitutionGuideBrand,
                                                                loalldPdfSize: loalldPdfSize,
                                                            }),
                                                        }}
                                                    />
                                                )}
                                            </TileSelect>
                                            <TileSelect
                                                icon="semantic-accessoires"
                                                title={cms('options.buy-a-car.headline')}
                                                inputType="radio"
                                                radioGroupName="contractEndMainOptions"
                                                testId={'tile-select-buy-a-car'}
                                                onClick={() => {
                                                    clearChosenOption();
                                                    setCurrentOptions(OptionsView.BUY_A_CAR);
                                                }}
                                            >
                                                {cms('options.buy-a-car.description')}
                                            </TileSelect>
                                            <TileSelect
                                                icon="semantic-reload"
                                                title={cms('options.return.headline')}
                                                inputType="radio"
                                                radioGroupName="contractEndMainOptions"
                                                onClick={() => handleChange(ContractEndOptionsEN.RETURN)}
                                            >
                                                {isRetail && vehicleRestitutionGuideBrand ? (
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: cms('options.return.vehicle-restitution-guide', {
                                                                brand: vehicleRestitutionGuideBrand,
                                                                loalldPdfSize: loalldPdfSize,
                                                            }),
                                                        }}
                                                    />
                                                ) : (
                                                    cms('options.return.description')
                                                )}
                                            </TileSelect>
                                        </TileSelectGroup>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {currentOptions === OptionsView.BUY_A_CAR && (
                        <>
                            {contract.isEligibleRenewalContractNotificationForLessOfTwoMonths ||
                            contract.isEligibleRenewalContractNotificationForLessOfOneMonth ? (
                                <PaymentRefinance2m1m
                                    contract={contract}
                                    onClickRefin={() => setCurrentOptions(OptionsView.REFINANCE_ADDITIONAL)}
                                    onClickPay={handleChange}
                                />
                            ) : (
                                <TileSelectGroup
                                    title={{ text: t('options.headline') }}
                                    key={OptionsView.BUY_A_CAR}
                                    layoutItemClassName={`u-1/${refinancingOption ? 2 : 1} u-1/2@l u-1/1@s`}
                                    className={'u-text-center'}
                                    testId={'tile-select-group-buy'}
                                >
                                    {refinancingOption}
                                    <TileSelect
                                        icon="semantic-fs-payment"
                                        title={cms('options.payment.headline')}
                                        inputType="radio"
                                        radioGroupName="buyACarOptions"
                                        onClick={() => handleChange(ContractEndOptionsEN.PAYMENT)}
                                    >
                                        {cms('options.payment.description')}
                                    </TileSelect>
                                </TileSelectGroup>
                            )}
                        </>
                    )}
                    {currentOptions === OptionsView.REFINANCE_ADDITIONAL && (
                        <RefinanceAdditional2m1m onClick={handleChange} contract={contract} />
                    )}
                </Layout.Item>
                <Layout.Item>
                    <ButtonContainer center>
                        <Button
                            secondary
                            type="button"
                            testId="backButton"
                            onClick={() => {
                                if (currentOptions === OptionsView.MAIN) redirectToDashboard();
                                else if (currentOptions === OptionsView.BUY_A_CAR) {
                                    clearChosenOption();
                                    setCurrentOptions(OptionsView.MAIN);
                                } else if (currentOptions === OptionsView.REFINANCE_ADDITIONAL) {
                                    clearChosenOption();
                                    setCurrentOptions(OptionsView.BUY_A_CAR);
                                }
                            }}
                        >
                            {t('buttons.back')}
                        </Button>
                        {showSubmitButton(contract) && (
                            <Button
                                testId="submitButton"
                                disabled={!contractEndOptionsRequest.optionValue}
                                type="button"
                                onClick={() => handleSubmit()}
                            >
                                {t('buttons.submit')}
                            </Button>
                        )}
                    </ButtonContainer>
                </Layout.Item>
            </Layout>
            <Modal
                shown={contractEndOptionsRequest.isSuccess}
                status="success"
                onClose={redirectToDashboard}
                onCancel={redirectToDashboard}
                buttonCancelText={t('success.button')}
                testId={'success-modal'}
                title={t('success.headline')}
                aria-live="polite"
                role="status"
                aria-atomic="true"
            >
                {getSuccessText(t, contractEndOptionsRequest, contract)}
            </Modal>
            <Modal
                shown={contractEndOptionsRequest.isError && !showEmailModal}
                status="error"
                onClose={handleModalClose}
                onCancel={handleModalClose}
                testId={'error-modal'}
                title={t('error.title')}
            >
                {t('error.text')}
            </Modal>
            <Formik
                initialValues={{ email: '' }}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .required(t('validation.email.required'))
                        .email(t('validation.email.mustBeEmail')),
                })}
                onSubmit={(values) => {
                    setShowEmailModal(false);
                    handleSubmit(values.email);
                }}
            >
                {(formik) => (
                    <EmailInputModal
                        show={showEmailModal}
                        onClose={() => setShowEmailModal(false)}
                        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                        onConfirm={formik.handleSubmit as any}
                    />
                )}
            </Formik>
        </>
    );
};

/// Card Components and Utils ////////////////////////////////////

const showLink = (brand: string) => ['vw', 'audi', 'seat', 'skoda', 'vmcv'].includes(brand?.toLowerCase() || '');
const getLink = (brand: string): string => {
    switch (brand.toLowerCase()) {
        case 'vw':
            return 'https://www.volkswagen.fr/fr.html';
        case 'audi':
            return 'https://www.audi.fr/fr/web/fr.html';
        case 'skoda':
            return 'https://www.skoda.fr/';
        case 'vwcv':
            return 'https://www.volkswagen-utilitaires.fr/fr.html';
        default:
            return '';
    }
};

export const RenewEndOption12Months: React.FC<{ onClick?: (option: ContractEndOptionsEN) => void; brand?: string }> = ({
    brand,
}) => {
    const { t } = useTranslation('contract-end-options');
    const { t: cms } = useTranslation('contract-end-options-cms');

    return (
        <div className="u-text-center">
            <Layout>
                <Layout.Item>
                    <Icon name="semantic-car" size={IconSize.LARGE} />
                    <Heading level={2}>{cms('options.renewal-12months.headline')}</Heading>
                    <Paragraph>{t('options.renewal-12months.description')}</Paragraph>
                </Layout.Item>
                {showLink(brand || '') && (
                    <Layout.Item>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: cms('options.renewal-12months.vehicle-restitution-guide', {
                                    brand: brand,
                                    link: getLink(brand || ''),
                                }),
                            }}
                        />
                    </Layout.Item>
                )}
                <Layout.Item>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: cms('options.renewal-12months.description2'),
                        }}
                    />
                </Layout.Item>
            </Layout>
        </div>
    );
};

export const RenewEndOption421Months: React.FC<{ onClick: (option: ContractEndOptionsEN) => void; brand?: string }> = ({
    onClick,
    brand,
}) => {
    const { t: cms } = useTranslation('contract-end-options-cms');

    return (
        <TileSelect
            icon="semantic-car"
            title={cms('options.renewal-4-2-1months.headline')}
            inputType="radio"
            radioGroupName="contractEndMainOptions"
            onClick={() => onClick(ContractEndOptionsEN.RENEWAL)}
            headingLevel={2}
        >
            <Layout>
                {showLink(brand || '') && (
                    <Layout.Item>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: cms('options.renewal-4-2-1months.vehicle-restitution-guide', {
                                    brand: brand,
                                    link: getLink(brand || ''),
                                }),
                            }}
                        />
                    </Layout.Item>
                )}

                <Layout.Item>
                    <span>{cms('options.renewal-4-2-1months.description')}</span>
                </Layout.Item>
                <Layout.Item>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: cms('options.renewal-4-2-1months.description2'),
                        }}
                    />
                </Layout.Item>
            </Layout>
        </TileSelect>
    );
};

export const BuyEndOption4m2m1m: React.FC<{ onClick: () => void; contract: Contract & ContractDetails }> = ({
    onClick,
    contract,
}) => {
    const { t: cms } = useTranslation('contract-end-options-cms');
    return (
        <TileSelect
            icon="semantic-accessoires"
            title={cms('options.buy-a-car-4months.headline')}
            headingLevel={2}
            inputType="radio"
            radioGroupName="contractEndMainOptions"
            testId={'tile-select-buy-a-car'}
            onClick={onClick}
        >
            <span
                dangerouslySetInnerHTML={{
                    __html: cms(
                        contract.isEligibleRenewalContractNotificationForLessOfFourMonths
                            ? 'options.buy-a-car-4months.description'
                            : 'options.buy-a-car-2-1months.description',
                        {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        },
                    ),
                }}
            />
        </TileSelect>
    );
};

export const ReturnEndOption4m2m1m: React.FC<{ onClick: (option: ContractEndOptionsEN) => void; one: boolean }> = ({
    onClick,
    one,
}) => {
    const { t: cms } = useTranslation('contract-end-options-cms');
    return (
        <TileSelect
            icon="semantic-reload"
            title={cms('options.return-4-2-1months.headline')}
            headingLevel={2}
            inputType="radio"
            radioGroupName="contractEndMainOptions"
            onClick={() => onClick(ContractEndOptionsEN.RETURN)}
        >
            <span
                dangerouslySetInnerHTML={{
                    __html: one
                        ? cms('options.return-4-2-1months.description1')
                        : cms('options.return-4-2-1months.description'),
                }}
            />
            <span
                dangerouslySetInnerHTML={{
                    __html: cms('options.return-4-2-1months.vehicle-restitution-guide'),
                }}
            />
        </TileSelect>
    );
};

// Option A / B for Buy option
export const PaymentRefinance2m1m: React.FC<{
    onClickRefin: () => void;
    onClickPay: (option: ContractEndOptionsEN) => void;
    contract: Contract & ContractDetails;
}> = ({ onClickRefin, onClickPay, contract }) => {
    const { t } = useTranslation('contract-end-options');
    const { t: cms } = useTranslation('contract-end-options-cms');
    return (
        <TileSelectGroup
            title={{ text: t('options.headline') }}
            key={OptionsView.BUY_A_CAR}
            layoutItemClassName={`u-1/2 u-1/2@l u-1/1@s`}
            className={'u-text-center'}
            testId={'tile-select-group-buy'}
        >
            <TileSelect
                icon="semantic-calculator"
                title={cms('options.refinancing-2-1months.headline')}
                headingLevel={2}
                inputType="radio"
                radioGroupName="buyACarOptions"
                onClick={onClickRefin}
                testId={'tile-select-refinancing'}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: cms('options.refinancing-2-1months.description', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            </TileSelect>
            <TileSelect
                icon="semantic-fs-payment"
                title={cms('options.payment-2-1months.headline')}
                headingLevel={2}
                inputType="radio"
                radioGroupName="buyACarOptions"
                onClick={() => onClickPay(ContractEndOptionsEN.PAYMENT)}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: cms('options.payment-2-1months.description', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            </TileSelect>
        </TileSelectGroup>
    );
};

export const RefinanceAdditional2m1m: React.FC<{
    onClick: (option: ContractEndOptionsEN) => void;
    contract: Contract & ContractDetails;
}> = ({ onClick, contract }) => {
    const { t } = useTranslation('contract-end-options');
    const { t: cms } = useTranslation('contract-end-options-cms');
    return (
        <TileSelectGroup
            title={{ text: t('options.headline') }}
            key={OptionsView.BUY_A_CAR}
            layoutItemClassName={`u-1/1 u-1/2@l u-1/1@s`}
            className={'u-text-center'}
            testId={'tile-select-group-buy'}
        >
            <TileSelect
                icon="semantic-calculator"
                title={cms('options.refinancing-2-1months.headline')}
                headingLevel={2}
                inputType="radio"
                radioGroupName="buyACarOptions"
                onClick={() => onClick(ContractEndOptionsEN.REFINANCING)}
                testId={'tile-select-refinancing'}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: cms('options.refinancing-2-1months.additional', {
                            lastPaymentInclTVA: contract.financial.lastPaymentInclTVA?.toLocaleString('fr-FR'),
                        }),
                    }}
                />
            </TileSelect>
        </TileSelectGroup>
    );
};
